import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Fingerprint, Target, Lightbulb, AlertTriangle, HelpCircle } from "lucide-react";

interface DecisionSnapshotProps {
  data: {
    problem_statement?: string;
    decision_question?: string;
    decision_question_original?: string;
    primary_tension?: string;
    primary_hypothesis?: string;
  };
}

export function DecisionSnapshot({ data }: DecisionSnapshotProps) {
  if (!data) return null;

  // Use original decision question if available (1:1 from Phase 1)
  const decisionQuestion = data.decision_question_original || data.decision_question;

  return (
    <Card id="decision-snapshot" className="scroll-mt-24 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Fingerprint className="h-5 w-5 text-primary" />
            Decision Snapshot
          </CardTitle>
          <Badge variant="outline" className="text-xs bg-primary/10 border-primary/30">
            Ankerdokument
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Aus Nutzereingabe – diese Elemente definieren den Entscheidungsrahmen
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Problemkern (1-2 Sätze) */}
        {data.problem_statement && (
          <div className="user-input">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h4 className="font-semibold text-sm">Problemkern</h4>
              <Badge variant="secondary" className="text-[10px] ml-auto">Nutzereingabe</Badge>
            </div>
            <p className="text-sm leading-relaxed pl-6">{data.problem_statement}</p>
          </div>
        )}
        {/* Spannungsfeld (A vs B + Kosten) */}
        {data.primary_tension && (
          <div className="system-derived bg-muted/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-orange-500" />
              <h4 className="font-semibold text-sm">Spannungsfeld</h4>
              <Badge variant="outline" className="text-[10px] ml-auto">Systemisch abgeleitet</Badge>
            </div>
            <p className="text-sm leading-relaxed pl-6">{data.primary_tension}</p>
          </div>
        )}

        {/* Kernhypothese (1 Satz, hervorgehoben) */}
        {data.primary_hypothesis && (
          <div className="user-input bg-primary/10 rounded-lg p-4 border-l-4 border-primary">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-primary" />
              <h4 className="font-semibold text-sm text-primary">Kernhypothese</h4>
              <Badge variant="secondary" className="text-[10px] ml-auto">Nutzereingabe</Badge>
            </div>
            <p className="text-sm font-medium leading-relaxed">{data.primary_hypothesis}</p>
          </div>
        )}

        {/* Entscheidungsfrage (prominent, verbatim) */}
        {decisionQuestion && (
          <div className="user-input bg-gradient-to-r from-primary/20 to-primary/10 rounded-lg p-4 border border-primary/30">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              <h4 className="font-semibold text-sm text-primary">Entscheidungsfrage</h4>
              <Badge className="text-[10px] ml-auto bg-primary/20 text-primary border-0">
                1:1 aus Phase 1
              </Badge>
            </div>
            <p className="text-base font-semibold leading-relaxed">
              "{decisionQuestion}"
            </p>
            <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-primary/20 italic">
              Diese Frage beantwortet der folgende Report.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
